import { PermissionGate } from "@components/shared/PermissionGate";
import { useAuth } from "@hooks/useAuth";
import useGzLog from "@hooks/useGzLog";
import { useScreens } from "@hooks/useScreens";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { BiImages, BiVideo } from "react-icons/bi";
import { BsReverseLayoutTextSidebarReverse } from "react-icons/bs";
import { FiMonitor } from "react-icons/fi";
import {
  MdAdminPanelSettings,
  MdLocationPin,
  MdPerson,
  MdPlaylistPlay,
} from "react-icons/md";
import { TbApps, TbTemplate } from "react-icons/tb";
import { PermissionGroup } from "types";

interface SidebarElement {
  name: string;
  url: string;
  icon: JSX.Element;
}

function SideBar() {
  const { user } = useAuth();
  const { screens } = useScreens();
  const { log } = useGzLog()
  const pathname = usePathname()!

  useEffect(() => {
    log({
      eventtype: "PageVisited",
      name: "PageVisited",
      payload: {
        pathname,
        page: pathname?.substring(1)
      }
    })
  }, [pathname])

  return (
    <div className="border-r border-muted bg-background pr-2 text-foreground">
      <div className="flex flex-col sm:flex-row sm:justify-around">
        <div className="flex h-[calc(100vh_-_3rem)] w-12 flex-col md:w-48">
          {/* <img src="dist/logo.svg" alt="logo" className="p-8 max-h-32" /> */}
          <nav className="h-full flex-1 overflow-y-auto">
            <SingleElement
              element={{
                name: "Screens",
                url: "/",
                icon: <FiMonitor />,
              }}
              permissionGroup="screens"
              currentPath={pathname}
            />
            {screens.length > 0 && (
              <SingleElement
                element={{
                  name: "Playlists",
                  url: "/playlists",
                  icon: <MdPlaylistPlay />,
                }}
                permissionGroup="playlists"
                currentPath={pathname}
              />
            )}
            {screens.length > 0 && (
              <SingleElement
                element={{
                  name: "Layouts",
                  url: "/layout",
                  icon: <BsReverseLayoutTextSidebarReverse />,
                }}
                permissionGroup="layouts"
                currentPath={pathname}
              />
            )}
            <SingleElement
              element={{
                name: "Your Uploads",
                url: "/assets",
                icon: <BiImages />,
              }}
              permissionGroup="assets"
              currentPath={pathname}
            />
            <SingleElement
              element={{
                name: "Your Videos",
                url: "/videos",
                icon: <BiVideo />,
              }}
              permissionGroup="videos"
              currentPath={pathname}
            />
            {screens.length > 0 && (
              <SingleElement
                element={{
                  name: "Templates",
                  url: "/template",
                  icon: <TbTemplate />,
                }}
                permissionGroup="templates"
                currentPath={pathname}
              />
            )}

            {screens.length > 0 && (
              <SingleElement
                element={{
                  name: "Locations",
                  url: "/locations",
                  icon: <MdLocationPin />,
                }}
                permissionGroup="locations"
                currentPath={pathname}
              />
            )}
            {screens.length > 0 && (
              <SingleElement
                element={{
                  name: "Apps",
                  url: "/apps",
                  icon: <TbApps />,
                }}
                permissionGroup="apps"
                currentPath={pathname}
              />
            )}

            <SingleElement
              element={{
                name: "Account Settings",
                url: "/profile",
                icon: <MdPerson />,
              }}
              currentPath={pathname}
            />

            {(user?.system_role === "admin" ||
              user?.system_role === "designer") && (
                <SingleElement
                  element={{
                    name: "Admin",
                    url: "/admin",
                    icon: <MdAdminPanelSettings />,
                  }}
                  currentPath={pathname}
                // subMenus={[
                //   user?.system_role === "admin" && {
                //     element: {
                //       name: "Thumbnails",
                //       url: "/admin/thumbnails",
                //       icon: <BiImages />,
                //     },
                //     currentPath: router.pathname,
                //   },

                //   {
                //     element: {
                //       name: "Public Templates",
                //       url: "/admin/public_templates",
                //       icon: <BiImages />,
                //     },
                //     currentPath: router.pathname,
                //   },
                // ]}
                />
              )}
          </nav>
        </div>
      </div>
    </div>
  );
}

interface SingleElementProps {
  permissionGroup?: PermissionGroup;
  element: SidebarElement;
  currentPath: string;
  subMenus?: (
    | {
      element: SidebarElement;
      currentPath: string;
    }
    | boolean
  )[];
}

function SingleElement(props: SingleElementProps) {
  return (
    <PermissionGate where={(ps) => !props.permissionGroup || ps.some(_p => _p.startsWith(props.permissionGroup!))}>
      <div className="flex flex-col">
        <SingleElementLink {...props} />

        {(props.subMenus ?? []).length > 0 && (
          <div className="flex flex-col pl-8">
            {props.subMenus?.map((menu) => {
              if (typeof menu === "boolean") return <></>;
              return (
                <SingleElementLink
                  key={`submenu_${menu.element.name}`}
                  currentPath={props.currentPath}
                  element={menu.element}
                />
              );
            })}
          </div>
        )}
      </div>
    </PermissionGate>
  );
}

function SingleElementLink(props: SingleElementProps) {
  const _isActive =
    getContentBeforeSecondSlash(props.currentPath) === props.element.url;
  return (
    <Link
      prefetch={false}
      href={props.element.url}
      key={props.element.name}
      className={`${_isActive
        ? "bg-primary/10 font-medium text-primary"
        : "font-normal hover:bg-primary/10 hover:text-primary"
        } ml-2 mt-2 flex cursor-pointer items-center gap-2 rounded-md py-2 pl-2 transition-colors duration-200 md:pl-4`}
    >
      <div
        className={`rounded-full bg-opacity-5 p-1 text-sm dark:bg-opacity-5 ${_isActive ? "bg-accentColor dark:bg-darkAccentColor" : "bg-gray-400"
          }`}
      >
        {props.element.icon}
      </div>
      <span className="hidden grow text-sm md:block">{props.element.name}</span>
    </Link>
  );
}

function getContentBeforeSecondSlash(input: string): string {
  const parts = input.split("/"); // Split the string by '/'

  // If there are at least two slashes, join the first two parts and return
  if (parts.length >= 2) {
    return parts.slice(0, 2).join("/");
  }

  // If there is no second slash, return the original string
  return input;
}

export default SideBar;
